import React from 'react';
import type { EvidenceSource } from '../../types/evidence';
import EvidenceSourceCard from './EvidenceSourceCard';
import { Database, PlusCircle } from 'lucide-react';

interface EvidenceSourceGridProps {
  sources: EvidenceSource[];
  onSync: (sourceId: number) => void;
  onEdit?: (source: EvidenceSource) => void;
  onDelete?: (sourceId: number) => void;
  syncingSourceIds: Set<number>;
  onAddSource?: () => void;
  isLoading?: boolean;
}

const EvidenceSourceGrid: React.FC<EvidenceSourceGridProps> = ({
  sources,
  onSync,
  onEdit,
  onDelete,
  syncingSourceIds,
  onAddSource,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, idx) => (
          <div key={idx} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 bg-gray-200 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (sources.length === 0) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-dashed border-blue-300 p-12 text-center">
        <Database className="h-20 w-20 text-blue-400 mx-auto mb-6" />
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          No Evidence Sources Connected
        </h3>
        <p className="text-gray-700 mb-4 max-w-2xl mx-auto text-lg">
          Start automated compliance evidence collection from multiple sources
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8 text-left">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-2xl mb-2">💻</div>
            <div className="font-semibold text-gray-900">Git Repositories</div>
            <div className="text-sm text-gray-600">GitHub, GitLab, Bitbucket</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-2xl mb-2">🔐</div>
            <div className="font-semibold text-gray-900">SSL Certificates</div>
            <div className="text-sm text-gray-600">SSL Labs scanning</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-2xl mb-2">☁️</div>
            <div className="font-semibold text-gray-900">Cloud Providers</div>
            <div className="text-sm text-gray-600">AWS, Azure, GCP</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-2xl mb-2">📊</div>
            <div className="font-semibold text-gray-900">SIEM Tools</div>
            <div className="text-sm text-gray-600">Splunk, Elasticsearch</div>
          </div>
        </div>

        {onAddSource && (
          <button
            onClick={onAddSource}
            className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <PlusCircle className="h-6 w-6" />
            Connect Your First Source
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {sources.map((source) => (
        <EvidenceSourceCard
          key={source.id}
          source={source}
          onSync={onSync}
          onEdit={onEdit}
          onDelete={onDelete}
          isSyncing={syncingSourceIds.has(source.id) || source.status === 'syncing'}
        />
      ))}
    </div>
  );
};

export default EvidenceSourceGrid;
